import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useContent } from "@/hooks/useContent";
import PageSEO from "@/components/PageSEO";

const IMG_HERO = "/assets/dubbi-hero.jpg";

const prototypes = [
  { title: "Apex Dubbi", subtitle: "Two-Level A-Frame Duplex", image: "/assets/dubbi/prototypes/apex-x.png", text: "A two-story A-frame Dubbi concept exploring a light footprint, natural ventilation and a strong connection to the cloud forest." },
  { title: "Square Dubbi", subtitle: "Two-Level Modular Duplex", image: "/assets/dubbi/prototypes/square-x.png", text: "A two-story modular Dubbi concept designed around efficient construction, adaptable interiors and repeatable components." },
  { title: "Circle / Octagon Dubbi", subtitle: "Two-Level Round / Multisided Duplex", image: "/assets/dubbi/prototypes/circle-octagon-x.png", text: "A two-story round or multisided Dubbi concept exploring natural light, panoramic views and an interior that flows with the surrounding landscape." },
  { title: "Cluster / Community", subtitle: "Dubbi Residences + Cabins/Casitas + Commons", image: "/assets/dubbi/prototypes/cluster-community-x.png", text: "A neighborhood-scale exploration showing how two-story Dubbis, one-story cabins/casitas and shared commons may relate through kitchens, gardens, paths and gathering spaces." },
];

const systems = [
  "Digestive",
  "Circulatory",
  "Nervous",
  "Respiratory",
  "Lymphatic",
  "Endocrine",
  "Urinary",
  "Reproductive",
  "Musculoskeletal",
];

const guilds = [
  "Natural Building",
  "Regenerative Agriculture",
  "Food Forest",
  "Music & Art",
  "Hospitality",
  "Land Stewardship",
];

export default function Dubbi() {
  const { t } = useContent("dubbi");

  const [form, setForm] = useState({
    name: "",
    email: "",
    interest: "Stay",
    message: "",
  });

  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setForm((current) => ({
      ...current,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await api.post("/dubbi/interest", form);
      toast.success("Thank you. Your interest has been received.");
      setForm({
        name: "",
        email: "",
        interest: "Stay",
        message: "",
      });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#F4F1ED] text-[#2C3D30]">
      <PageSEO
        title="Cloud Forrest EcoVillage | Dubbi Town | Celestial Seed Sanctuary"
        description="Cloud Forrest EcoVillage, also known as Dubbi Town, is a neighborhood within the greater Celestial Seed Sanctuary in the Colombian cloud forest. Dubbi is the structure at the heart of this evolving neighborhood concept."
      />

      {/* HERO */}
      <section className="relative min-h-[82vh] overflow-hidden">
        <img
          src={IMG_HERO}
          alt="Cloud forest cabin at sunset — Celestial Seed Sanctuary"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />

        <div className="relative z-10 mx-auto flex min-h-[82vh] max-w-7xl items-end px-6 pb-16 md:px-10 md:pb-24">
          <div className="max-w-4xl text-white">
            <p className="mb-5 text-sm uppercase tracking-[0.3em]">
              Cloud Forrest EcoVillage · Dubbi Town
            </p>

            <h1 className="text-5xl font-light leading-[0.95] md:text-7xl lg:text-8xl">
              More Than
              <br />
              Four Walls.
            </h1>

            <p className="mt-8 max-w-2xl text-lg leading-relaxed md:text-xl">
              A Dubbi is your private home base inside a living New Earth
              ecology — where land, food, community, learning, culture and
              regeneration meet.
            </p>

            <a
              href="#vision"
              className="mt-9 inline-block border border-white px-7 py-3 text-sm uppercase tracking-[0.18em] transition hover:bg-white hover:text-[#2C3D30]"
            >
              Step Inside
            </a>
          </div>
        </div>
      </section>

      {/* GRAND VISION */}
      <section id="vision" className="px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-[#575A57]">
            The Grand Vision
          </p>

          <h2 className="mt-5 max-w-5xl text-4xl font-light leading-tight md:text-6xl">
            An incubator for a New Earth.
          </h2>

          <div className="mt-12 grid gap-10 md:grid-cols-2">
            <p className="text-xl leading-relaxed">
              Celestial Seed is a living sanctuary rooted in a Colombian cloud
              forest — an integrated habitat where regenerative land
              stewardship, community, natural building, food, culture,
              learning, Guilds and embodied practice become part of everyday
              life.
            </p>

            <p className="leading-8 text-[#575A57]">
              Dubbi is not the entire project. It is a doorway into the larger
              ecosystem: the People&apos;s Sanctuary, Ewok City, the Commons,
              gardens and food forest, communal spaces, the Living Calendar,
              Guilds, retreats, music, art and the evolving culture of
              Celestial Seed.
            </p>
          </div>
        </div>
      </section>

      {/* LAND */}
      <section className="bg-[#2C3D30] px-6 py-20 text-white md:px-10">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-white/60">
            The Land
          </p>

          <div className="mt-8 grid gap-10 md:grid-cols-3">
            <div>
              <div className="text-5xl font-light">34,500 m²</div>
              <p className="mt-3 text-white/65">Cloud-forest land</p>
            </div>

            <div>
              <div className="text-5xl font-light">8.5 acres</div>
              <p className="mt-3 text-white/65">Across Lots 1, 5 & 6</p>
            </div>

            <div>
              <div className="text-5xl font-light">Silvania</div>
              <p className="mt-3 text-white/65">
                Cundinamarca · Colombia
              </p>
            </div>
          </div>

          <p className="mt-12 max-w-3xl leading-8 text-white/75">
            The land is the foundation. Forest, water, gardens, paths,
            structures, food systems and gathering spaces form the physical
            ecology from which the larger sanctuary grows.
          </p>

          <figure className="mt-12">
            <img
              src="/assets/dubbi-map.png"
              alt="Celestial Seed Sanctuary — aerial context showing Lots 1, 5 and 6"
              className="w-full rounded-md border border-white/15"
              data-testid="dubbi-land-map"
            />
            <figcaption className="mt-3 text-sm text-white/60">
              Aerial context — Celestial Seed Sanctuary Lots 1, 5 and 6. Shown for parcel and legal context only; not an approved development plan.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* WHAT IS A DUBBI */}
      <section className="px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-[#575A57]">
            What Is A Dubbi?
          </p>

          <h2 className="mt-5 max-w-4xl text-4xl font-light md:text-6xl">
            A small duplex home. A private place within a shared world.
          </h2>

          <p className="mt-10 max-w-3xl text-lg leading-8 text-[#575A57]">
            Dubbi takes its name from the small duplex concept. Each home is
            envisioned as a simple, beautiful base from which residents and
            guests can participate in the wider life of Celestial Seed.
          </p>

          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Nest",
                text: "One private Dubbi — your personal home base within the sanctuary.",
              },
              {
                title: "Kin",
                text: "A small cluster of Dubbis sharing communal space and kitchen facilities.",
              },
              {
                title: "Village",
                text: "The wider 20-Dubbi concept connected through the central Commons.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="border border-[#2C3D30]/20 bg-white p-8"
              >
                <h3 className="text-3xl font-light">{item.title}</h3>
                <p className="mt-5 leading-7 text-[#575A57]">{item.text}</p>
              </div>
            ))}
          </div>

          <figure className="mt-16">
            <img
              src="/assets/dubbi-home.jpg"
              alt="Interior of a Dubbi home — bed with cloud-forest and mountain view"
              className="w-full rounded-md"
              data-testid="dubbi-home-base-image"
            />
            <figcaption className="mt-3 text-sm text-[#575A57]">
              Home base — the private interior of a Dubbi, opening onto the cloud forest at sunrise.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* COMMONS */}
      <section className="bg-[#E5E1D8] px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-[#575A57]">
            The People&apos;s Sanctuary
          </p>

          <h2 className="mt-5 max-w-4xl text-4xl font-light md:text-6xl">
            The Commons is the heart.
          </h2>

          <div className="mt-14 grid gap-x-12 gap-y-8 md:grid-cols-3">
            {[
              "Communal kitchens",
              "Gardens & food forest",
              "Paths & bridges",
              "Gathering spaces",
              "Art & music",
              "Quiet nooks",
              "Shared meals",
              "Workshops",
              "Land stewardship",
            ].map((item) => (
              <div
                key={item}
                className="border-t border-[#2C3D30]/25 pt-5 text-lg"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVING CALENDAR */}
      <section className="px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-[#575A57]">
            The Living Calendar
          </p>

          <h2 className="mt-5 max-w-4xl text-4xl font-light md:text-6xl">
            Life moves with the land, the moon and the community.
          </h2>

          <div className="mt-16 grid gap-6 md:grid-cols-4">
            {[
              ["New Moon", "Intention · initiation · reset"],
              ["Waxing", "Nourishment · building · activation"],
              ["Full Moon", "Culmination · celebration · community"],
              ["Waning", "Integration · release · rest"],
            ].map(([title, text]) => (
              <div key={title} className="border-t border-[#2C3D30] pt-6">
                <h3 className="text-2xl">{title}</h3>
                <p className="mt-3 leading-7 text-[#575A57]">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* META DIET */}
      <section className="bg-[#6d7657] px-6 py-24 text-white md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-white/60">
            Year-Round Transformational Ecology
          </p>

          <h2 className="mt-5 max-w-5xl text-4xl font-light md:text-6xl">
            Meta-Diet · Living Medicine Wheel
          </h2>

          <p className="mt-9 max-w-3xl text-lg leading-8 text-white/75">
            A year-round educational and embodied rhythm exploring the
            relationship between food, movement, land, rest, community and
            nine systems of the human body.
          </p>

          <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3">
            {systems.map((system, index) => (
              <div
                key={system}
                className="border border-white/20 p-5 md:p-6"
              >
                <span className="text-xs text-white/50">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="mt-2 text-lg">{system}</div>
              </div>
            ))}
          </div>

          <p className="mt-8 max-w-3xl text-sm leading-6 text-white/55">
            These themes are presented as educational, cultural and wellness
            programming. They are not medical diagnosis or treatment.
          </p>
        </div>
      </section>

      {/* THREE CLOCKS */}
      <section className="px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-[#575A57]">
            A Living Rhythm
          </p>

          <div className="mt-10 grid gap-8 md:grid-cols-3">
            <div>
              <div className="text-sm uppercase tracking-[0.2em]">Daily</div>
              <h3 className="mt-4 text-3xl font-light">Field</h3>
              <p className="mt-5 leading-7 text-[#4A5D4E]">
                Movement, breath, land work, Guild practice, shared meals,
                meditation, music, integration and quiet.
              </p>
            </div>

            <div>
              <div className="text-sm uppercase tracking-[0.2em]">Lunar</div>
              <h3 className="mt-4 text-3xl font-light">Experience</h3>
              <p className="mt-5 leading-7 text-[#4A5D4E]">
                Monthly themes connecting body, food, elemental learning,
                gatherings, celebration and reflection.
              </p>
            </div>

            <div>
              <div className="text-sm uppercase tracking-[0.2em]">
                Multi-Year
              </div>
              <h3 className="mt-4 text-3xl font-light">Deep Current</h3>
              <p className="mt-5 leading-7 text-[#4A5D4E]">
                Earth · Water · Fire · Air — a longer arc for deeper learning,
                stewardship and cultural continuity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GUILDS */}
      <section className="bg-[#ece4d3] px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-[#575A57]">
            Learn By Doing
          </p>

          <h2 className="mt-5 max-w-4xl text-4xl font-light md:text-6xl">
            The Guilds turn knowledge into living practice.
          </h2>

          <div className="mt-14 grid gap-4 md:grid-cols-2">
            {guilds.map((guild) => (
              <div
                key={guild}
                className="flex items-center justify-between border-b border-[#2C3D30]/25 py-5 text-xl"
              >
                <span>{guild}</span>
                <span>→</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CULTURE */}
      <section className="bg-[#1A1D1A] px-6 py-24 text-white md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-white/50">
            A Year-Round Culture
          </p>

          <h2 className="mt-5 max-w-5xl text-4xl font-light md:text-6xl">
            Music. Art. Retreats. Learning. Celebration. Rest.
          </h2>

          <p className="mt-9 max-w-3xl text-lg leading-8 text-white/70">
            Rather than a disconnected series of events, Celestial Seed is
            envisioned as an evolving cultural ecology — a place where
            gatherings, workshops, retreats, creativity and community become
            part of the rhythm of the land.
          </p>
        </div>
      </section>

      {/* WAYS TO BELONG */}
      <section className="px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-[#575A57]">
            Ways To Belong
          </p>

          <div className="mt-12 space-y-0">
            {[
              ["01", "Stay", "Experience Celestial Seed from a private home base."],
              ["02", "Participate", "Enter the Commons, gardens and living calendar."],
              ["03", "Immerse", "Join deeper retreats and transformational programs."],
              ["04", "Learn & Practice", "Develop skills through Guilds and workshops."],
              ["05", "Steward & Reside", "Explore deeper responsibility and long-term participation."],
            ].map(([number, title, text]) => (
              <div
                key={number}
                className="grid gap-3 border-t border-[#2C3D30]/25 py-7 md:grid-cols-[100px_260px_1fr]"
              >
                <span className="text-[#575A57]">{number}</span>
                <h3 className="text-2xl">{title}</h3>
                <p className="max-w-xl leading-7 text-[#4A5D4E]">{text}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 max-w-3xl text-sm leading-6 text-[#575A57]">
            Sacred, private and ceremonial spaces remain permission-based.
            Booking or purchasing accommodation does not automatically grant
            access to every activity or space.
          </p>
        </div>
      </section>

      {/* ARCHITECTURE */}
      <section className="bg-[#d7c6a5] px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em]">
            Natural Building
          </p>

          <h2 className="mt-5 max-w-4xl text-4xl font-light md:text-6xl">
            Built from the language of the land.
          </h2>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {prototypes.map((prototype) => (
              <article key={prototype.title} className="overflow-hidden border border-[#2C3D30]/25 bg-[#F4F1ED]">
                <div className="aspect-[4/3] overflow-hidden bg-[#c7b895]">
                  <img src={prototype.image} alt={`${prototype.title} Dubbi architectural concept rendering`} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="p-7">
                  <h3 className="text-3xl font-light">{prototype.title}</h3>
                  <p className="mt-2 text-sm uppercase tracking-[0.14em] text-[#575A57]">{prototype.subtitle}</p>
                  <p className="mt-5 leading-7 text-[#575A57]">{prototype.text}</p>
                </div>
              </article>
            ))}
          </div>

          <p className="mt-10 max-w-4xl leading-8">
            Material vocabulary includes guadua and bamboo, cob, reclaimed
            timber and wooden pallets, with traditional palm-thatch roofing
            alongside practical metal or other appropriate roofing systems.
            Final structural systems remain subject to engineering,
            environmental conditions and legal approval.
          </p>

          <figure className="mt-12">
            <img
              src="/assets/dubbi-material.jpg"
              alt="Guadua and bamboo structural columns at Celestial Seed"
              className="w-full rounded-md"
              data-testid="dubbi-materials-image"
            />
            <figcaption className="mt-3 text-sm">
              Guadua and bamboo columns — the load-bearing language of natural building on site.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* 20 DUBBI MODEL */}
      <section className="px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.28em] text-[#575A57]">
            The Village Model
          </p>

          <h2 className="mt-5 text-5xl font-light md:text-7xl">
            20 Dubbis.
          </h2>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            <div className="border-t border-[#2C3D30] pt-6">
              <div className="text-5xl font-light">10</div>
              <p className="mt-3">Planned rental homes</p>
            </div>

            <div className="border-t border-[#2C3D30] pt-6">
              <div className="text-5xl font-light">6</div>
              <p className="mt-3">Planned shared-access / timeshare concept</p>
            </div>

            <div className="border-t border-[#2C3D30] pt-6">
              <div className="text-5xl font-light">4</div>
              <p className="mt-3">Planned future-sale homes</p>
            </div>
          </div>

          <p className="mt-10 max-w-4xl text-sm leading-7 text-[#575A57]">
            This is the current development concept, not an offer for sale.
            Final unit count, locations, construction, rentals, shared-access
            arrangements and future sales depend on zoning, title,
            environmental, infrastructure, sanitation, engineering and other
            applicable approvals.
          </p>
        </div>
      </section>

      {/* INTEREST */}
      <section
        id="interest"
        className="bg-[#2C3D30] px-6 py-24 text-white md:px-10 md:py-32"
      >
        <div className="mx-auto grid max-w-6xl gap-16 md:grid-cols-2">
          <div>
            <p className="text-sm uppercase tracking-[0.28em] text-white/55">
              Step Inside
            </p>

            <h2 className="mt-5 text-4xl font-light md:text-6xl">
              Follow the evolution of Dubbi.
            </h2>

            <p className="mt-8 max-w-xl leading-8 text-white/70">
              Tell us how you would like to experience Celestial Seed. This
              form records interest only and does not create a reservation,
              purchase, investment or ownership agreement.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-white">
                Name
              </Label>
              <Input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className="mt-2 border-white/25 bg-transparent text-white"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-white">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                className="mt-2 border-white/25 bg-transparent text-white"
              />
            </div>

            <div>
              <Label htmlFor="interest" className="text-white">
                I&apos;m interested in
              </Label>

              <select
                id="interest"
                name="interest"
                value={form.interest}
                onChange={handleChange}
                className="mt-2 w-full border border-white/25 bg-[#2C3D30] px-3 py-3 text-white"
              >
                <option>Stay</option>
                <option>Shared-Access / Timeshare Interest</option>
                <option>Future Purchase Interest</option>
                <option>Guilds & Workshops</option>
                <option>Retreats & Programs</option>
                <option>General Interest</option>
              </select>
            </div>

            <div>
              <Label htmlFor="message" className="text-white">
                Message
              </Label>
              <Textarea
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={5}
                className="mt-2 border-white/25 bg-transparent text-white"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="border border-white px-7 py-3 text-sm uppercase tracking-[0.18em] transition hover:bg-white hover:text-[#2C3D30] disabled:opacity-50"
            >
              {sending ? "Sending..." : "Register Interest"}
            </button>
          </form>
        </div>
      </section>

      {/* STATUS */}
      <section className="bg-[#1A1D1A] px-6 py-10 text-white md:px-10">
        <div className="mx-auto max-w-6xl text-xs leading-6 text-white/50">
          Dubbi and the wider ecovillage shown here represent the evolving
          Celestial Seed vision. Architectural prototypes, village layouts,
          access models and future development remain conceptual until the
          applicable legal, environmental, engineering and infrastructure
          requirements are verified and approved.
        </div>
      </section>
    </div>
  );
}